import * as React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  Button,
  Skeleton,
  Tooltip,
  AddIcon,
  VideocamIcon,
  VideoLibraryIcon,
  DeleteIcon,
} from "../../themeV2";
import {
  STATUS,
  failureOf,
  isInFlight,
  isSettling,
  uploadErrorCopy,
} from "../../domain/mediaUpload";
import { useInfluencer } from "../../hooks";
import useMediaUpload from "../../hooks/useMediaUpload";
import { useNotification } from "../../services/Notifications";
import MediaProgress from "../MediaCapture/MediaProgress";
import VideoAvatar from "./VideoAvatar";
import * as service from "./service";
import css from "./VideoAvatarEditor.module.css";

const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const UPLOAD_TYPE = "influencer_video_avatar";

const VideoAvatarEditor = React.memo(function VideoAvatarEditor({
  influencerId,
}) {
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [uploadError, setUploadError] = React.useState(null);

  const {
    influencer,
    isLoading: isLoadingI,
    isError: isErrorI,
    refresh,
  } = useInfluencer({
    influencerId,
  });

  const {
    upload,
    state: uploadState,
    cancel: cancelUpload,
    reset,
  } = useMediaUpload();

  const notify = useNotification();

  const isLoading = isLoadingI;
  const isError = isErrorI;
  const hasVideo = Boolean(influencer?.hasVideoAvatar);
  const videoAvatarUrl = influencer?.videoAvatarUrl;

  const avatar = React.useMemo(() => {
    if (isLoading) {
      return <Skeleton className={css.skeleton} />;
    }

    if (hasVideo) {
      return <VideoAvatar influencer={influencer} />;
    }

    return (
      <div
        className={css.emptyState}
        role="button"
        tabIndex={0}
        onClick={() => setDialogOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setDialogOpen(true);
          }
        }}
      >
        <p className={css.emptyStateText}>
          Add a Video Message to your fans inviting them to buy an autograph
        </p>
      </div>
    );
    // influencer is covered by the more stable videoAvatarUrl + hasVideo primitives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasVideo, videoAvatarUrl]);

  const influencerRef = React.useRef(influencer);
  influencerRef.current = influencer;
  const refreshRef = React.useRef(refresh);
  refreshRef.current = refresh;

  React.useEffect(() => {
    if (uploadState.status === STATUS.COMPLETE && uploadState.processedUrl) {
      refreshRef.current(
        influencerRef.current.mutateRawValues({
          has_video_avatar: 1,
          video_avatar_url: uploadState.processedUrl,
        }),
      );
      reset();
    }
  }, [uploadState.status, uploadState.processedUrl, reset]);

  /* The dialog is long closed by the time processing fails, so the only place
     left to say so is here. */
  React.useEffect(() => {
    if (uploadState.status !== STATUS.FAILED) return;

    notify({
      message: uploadErrorCopy({ ...uploadState, uploadType: UPLOAD_TYPE }),
      type: "error",
      duration: 0,
    });
    reset();
  }, [uploadState, notify, reset]);

  /* ── Upload dialog helpers ── */

  const isUploading = isInFlight(uploadState.status);

  const resetUploadState = React.useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError(null);
  }, []);

  const handleUploadClose = React.useCallback(
    (open) => {
      if (open) return;
      if (isUploading) {
        cancelUpload();
      }
      setUploadOpen(false);
      resetUploadState();
    },
    [isUploading, cancelUpload, resetUploadState],
  );

  const handleFileChange = React.useCallback((evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;

    const isValidType = ACCEPTED_VIDEO_TYPES.some((type) =>
      file.type.startsWith(type.split("/")[0]),
    );

    if (!isValidType) {
      setUploadError("Please select a valid video file (.mov or .mp4).");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = Math.round((file.size / 1048576) * 10) / 10;
      setUploadError(
        `File is too large (${sizeMB} MB). Maximum size is ${MAX_FILE_SIZE_MB} MB.`,
      );
      return;
    }

    setUploadError(null);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }, []);

  const handleUploadSubmit = React.useCallback(async () => {
    if (!selectedFile || isLoading || isError) return;

    setUploadError(null);

    try {
      await upload(selectedFile, {
        uploadType: UPLOAD_TYPE,
        referenceId: influencerId,
        influencerId,
        mimeType: selectedFile.type,
      });

      setUploadOpen(false);
      resetUploadState();
    } catch (err) {
      setUploadError(
        uploadErrorCopy({ ...failureOf(err), uploadType: UPLOAD_TYPE }),
      );
    }
  }, [
    selectedFile,
    isLoading,
    isError,
    upload,
    influencerId,
    resetUploadState,
  ]);

  /* ── Options dialog helpers ── */

  const handleDialogClose = React.useCallback((open) => {
    if (!open) setDialogOpen(false);
  }, []);

  const handleUploadClick = React.useCallback(() => {
    setDialogOpen(false);
    resetUploadState();
    setUploadOpen(true);
  }, [resetUploadState]);

  const handleRemoveClick = React.useCallback(() => {
    setDialogOpen(false);
    setConfirmRemoveOpen(true);
  }, []);

  /* ── Remove confirmation helpers ── */

  const handleRemoveOpenChange = React.useCallback((open) => {
    if (!open) setConfirmRemoveOpen(false);
  }, []);

  const handleRemoveConfirm = React.useCallback(async () => {
    setConfirmRemoveOpen(false);

    if (!influencer) return;

    refresh(
      influencer.mutateRawValues({
        has_video_avatar: 0,
        video_avatar_url: null,
      }),
    );

    await service.removeVideoAvatar({ influencerId: influencer.influencerId });
    refresh();
  }, [influencer, refresh]);

  const handleRemoveCancel = React.useCallback(() => {
    setConfirmRemoveOpen(false);
  }, []);

  // Covers the queue wait too, so the overlay does not vanish between the
  // upload landing and a worker picking the job up.
  const isProcessing = isSettling(uploadState.status);

  return (
    <div className={css.wrapper}>
      {avatar}

      {isProcessing && (
        <div className={css.processingOverlay}>
          <MediaProgress state={uploadState} />
        </div>
      )}

      <Tooltip.Root>
        <Tooltip.Trigger
          render={
            <button
              type="button"
              className={css.triggerButton}
              disabled={isProcessing}
              onClick={() => setDialogOpen(true)}
              aria-label="Video options"
            />
          }
        >
          <VideocamIcon />
        </Tooltip.Trigger>
        <Tooltip.Popup>Video options</Tooltip.Popup>
      </Tooltip.Root>

      {/* ── Options dialog ── */}
      <Dialog.Root open={dialogOpen} onOpenChange={handleDialogClose}>
        <Dialog.Popup size="sm">
          <Dialog.Title>Video message</Dialog.Title>
          <Dialog.Close />

          <div className={css.dialogOptionList}>
            <button
              type="button"
              className={css.dialogOption}
              onClick={handleUploadClick}
            >
              <VideoLibraryIcon />
              <span>
                {hasVideo ? "Upload new video" : "Upload video"}
                <br />
                <span className={css.dialogOptionTextSecondary}>
                  Accepted: .mov, .mp4 — max 25 MB
                </span>
              </span>
            </button>

            {hasVideo && (
              <button
                type="button"
                className={css.dialogOption}
                onClick={handleRemoveClick}
              >
                <DeleteIcon />
                Remove video
              </button>
            )}
          </div>
        </Dialog.Popup>
      </Dialog.Root>

      {/* ── Upload dialog ── */}
      <Dialog.Root open={uploadOpen} onOpenChange={handleUploadClose}>
        <Dialog.Popup size="md">
          <Dialog.Title>Upload video message</Dialog.Title>
          <Dialog.Close />

          <div className={css.uploadContent}>
            <p className={css.uploadDescription}>
              Select a video file to upload.
            </p>

            {previewUrl && (
              <video
                src={previewUrl}
                className={css.videoPreview}
                controls={false}
                muted
              />
            )}

            <Button
              variant="tonal"
              as="label"
              leading={<AddIcon />}
              disabled={isUploading}
            >
              {selectedFile ? "Change file" : "Select file"}
              <input
                type="file"
                accept="video/*"
                hidden
                onChange={handleFileChange}
                disabled={isUploading}
              />
            </Button>

            <p className={css.helperText}>
              Accepted: .mov, .mp4 — max {MAX_FILE_SIZE_MB} MB
            </p>

            {isUploading && <MediaProgress state={uploadState} />}

            {Boolean(uploadError) && <p className={css.error}>{uploadError}</p>}
          </div>

          <div className={css.actions}>
            <Button
              variant="text"
              onClick={() => handleUploadClose(false)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleUploadSubmit}
              disabled={isUploading || !selectedFile}
              loading={isUploading}
            >
              Upload
            </Button>
          </div>
        </Dialog.Popup>
      </Dialog.Root>

      {/* ── Remove confirmation dialog ── */}
      <Dialog.Root
        alert
        open={confirmRemoveOpen}
        onOpenChange={handleRemoveOpenChange}
      >
        <Dialog.Popup size="sm">
          <Dialog.Title>Remove video?</Dialog.Title>
          <Dialog.Description>
            This will permanently remove your video message. Visitors will see
            your profile photo instead.
          </Dialog.Description>

          <div className={css.actions}>
            <Button variant="text" onClick={handleRemoveCancel}>
              Cancel
            </Button>
            <Button variant="filled" onClick={handleRemoveConfirm}>
              Remove
            </Button>
          </div>
        </Dialog.Popup>
      </Dialog.Root>
    </div>
  );
});

VideoAvatarEditor.propTypes = {
  influencerId: PropTypes.number.isRequired,
};

export default VideoAvatarEditor;
